import React, { CSSProperties, useMemo } from 'react';
import { Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FolderIcon from '@material-ui/icons/Folder';
import SubdirectoryArrowRightIcon from '@material-ui/icons/SubdirectoryArrowRight';
import { TreeItem, TreeView } from '@material-ui/lab';
import { DefaultFolderNode } from '../../api-client/autogenerated';
import { FOLDER_ICON_COLOR, PROJECT_NAME_PLACEHOLDER } from '../../scripts/constants';

/** The project's own folder, which everything else sits inside, as it does in the File Manager. */
const PROJECT_NODE_ID = '/';

/** Every folder path in the structure, so the tree opens fully expanded. */
const collectNodeIds = (folders: DefaultFolderNode[], prefix = '/'): string[] =>
  folders.flatMap((folder) => {
    const nodeId = `${prefix}${folder.name}/`;
    return [nodeId, ...collectNodeIds(folder.children || [], nodeId)];
  });

const useStyles = makeStyles(() =>
  createStyles({
    treeItemLabel: {
      display: 'flex',
      alignItems: 'center',
      padding: '4px 0px',
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    folderIcon: {
      color: FOLDER_ICON_COLOR,
      marginRight: 8,
    },
    subfolderIcon: {
      color: '#7A797A',
      marginRight: 4,
    },
    projectPlaceholder: {
      fontStyle: 'italic',
      color: '#7A797A',
    },
  }),
);

type FolderStructureTreeProps = {
  folders: DefaultFolderNode[];
  /** Names the root folder. Falls back to a placeholder when no project is in hand. */
  projectName?: string;
  style?: CSSProperties;
};

/**
 * Read-only view of a default folder structure, laid out the way the File Manager lays out a
 * project's folders: the project at the root, with the structure's folders inside it.
 */
export default function FolderStructureTree(props: FolderStructureTreeProps) {
  const { folders, projectName, style } = props;
  const classes = useStyles();

  const expandedNodeIds = useMemo(() => [PROJECT_NODE_ID, ...collectNodeIds(folders)], [folders]);

  const renderFolders = (level: DefaultFolderNode[], prefix = '/', depth = 0) =>
    level.map((folder) => {
      const nodeId = `${prefix}${folder.name}/`;
      return (
        <TreeItem
          key={nodeId}
          nodeId={nodeId}
          classes={{ label: classes.treeItemLabel }}
          label={
            <>
              {depth > 0 ? <SubdirectoryArrowRightIcon className={classes.subfolderIcon} /> : null}
              <FolderIcon className={classes.folderIcon} />
              <Typography variant="body1">{folder.name}</Typography>
            </>
          }
        >
          {renderFolders(folder.children || [], nodeId, depth + 1)}
        </TreeItem>
      );
    });

  return (
    <TreeView
      defaultCollapseIcon={<ExpandMoreIcon />}
      defaultExpandIcon={<ChevronRightIcon />}
      expanded={expandedNodeIds}
      selected={[]}
      style={style}
    >
      <TreeItem
        nodeId={PROJECT_NODE_ID}
        classes={{ label: classes.treeItemLabel }}
        label={
          <>
            <FolderIcon className={classes.folderIcon} />
            <Typography
              variant="body1"
              className={projectName ? undefined : classes.projectPlaceholder}
            >
              {projectName || PROJECT_NAME_PLACEHOLDER}
            </Typography>
          </>
        }
      >
        {renderFolders(folders)}
      </TreeItem>
    </TreeView>
  );
}
